import React, { useCallback, useEffect, useRef, useState } from "react";
import "../css/HomePage.css";
import { useLocation, useNavigate } from "react-router-dom";
import { clearCurrentUser, getCurrentUser } from "../auth/session";
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  deleteDoc,
  doc,
  limit,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import {
  createFoodOrderBatch,
  sanitizeFoodOrders,
  upsertFoodOrderLine,
} from "../utils/foodOrders";
import { CARD_FEE_RATE } from "../utils/bookingCalculations";
import DailyTransactions from "./DailyTransactions";
import PrivateEvents from "./PrivateEvents";
import PrivateEventLeads from "./PrivateEventLeads";
import MonthlyReport from "./MonthlyReport";
import DailyAnalysis from "./DailyAnalysis";
import FoodReport from "./FoodReport";
import CustomerInfo from "./CustomerInfo";
import BusinessTrends from "./BusinessTrends";
import InvoiceGenerator from "./InvoiceGenerator";
import DepositConfirmationGenerator from "./DepositConfirmationGenerator";
import Tracker from "./Tracker";
import HolidayInsights from "./HolidayInsights";
import ExpensesPanel from "./ExpensesPanel";
import Users from "./Users";
import {
  mapCalendarBookingToDepositPrefill,
  mapCalendarBookingToInvoicePrefill,
} from "../utils/eventCalendarDocumentPrefill";
import {
  FaBars,
  FaCalendarAlt,
  FaChartBar,
  FaChevronDown,
  FaChevronRight,
  FaClipboardList,
  FaFileInvoiceDollar,
  FaHome,
  FaReceipt,
  FaUserFriends,
  FaUsers,
  FaUtensils,
} from "react-icons/fa";

const VALID_HOME_TABS = new Set([
  "home",
  "foodOrders",
  "dailyTransactions",
  "privateEvents",
  "privateEventNotes",
  "tracker",
  "invoiceGenerator",
  "depositConfirmationGenerator",
  "monthlyReport",
  "dailyAnalysis",
  "foodReport",
  "businessTrends",
  "holidayInsights",
  "expenses",
  "inventory",
  "customerInfo",
  "users",
]);

const DOCUMENT_TABS = new Set([
  "invoiceGenerator",
  "depositConfirmationGenerator",
]);

const getTabFromSearch = (search) => {
  const params = new URLSearchParams(search);
  const nextTab = params.get("tab");
  return VALID_HOME_TABS.has(nextTab) ? nextTab : "home";
};

const getDocumentEventIdFromSearch = (search) => {
  const params = new URLSearchParams(search);
  return params.get("eventId") || "";
};

const HomePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(() => getCurrentUser());
  const isAdmin = currentUser?.role === "admin";

  useEffect(() => {
    if (!currentUser) navigate("/");
  }, [currentUser, navigate]);

  function normalizePhone(value) {
    return String(value || "").replace(/\D/g, "");
  }

  const parseTxnDateTime = (txn) => {
    const d = txn?.Date;
    const t = txn?.Time;
    if (!d || !t) return 0;
    const dt = new Date(`${d} ${t}`);
    const ms = dt.getTime();
    return Number.isNaN(ms) ? 0 : ms;
  };

  const loadTransactionsForDate = useCallback(async (dateString) => {
    const targetDate = dateString || new Date().toLocaleDateString();
    try {
      const snap = await getDocs(
        query(collection(db, "transactions"), where("Date", "==", targetDate))
      );
      const fetchedTransactions = snap.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));
      setTransactions(fetchedTransactions);
      console.info(
        "HomePage transactions read for date:",
        targetDate,
        fetchedTransactions.length
      );
      return fetchedTransactions;
    } catch (error) {
      console.error("Error fetching transactions by date:", error);
      return [];
    }
  }, []);

  const queryTransactionsByPhone = useCallback(async (phoneRaw, maxDocs = 25) => {
    const possiblePhones = Array.from(
      new Set([phoneRaw, normalizePhone(phoneRaw)].filter(Boolean))
    );
    const docsById = new Map();

    await Promise.all(
      possiblePhones.map(async (phone) => {
        try {
          const snap = await getDocs(
            query(
              collection(db, "transactions"),
              where("PhoneNum", "==", phone),
              limit(maxDocs)
            )
          );
          snap.docs.forEach((item) => {
            docsById.set(item.id, { id: item.id, ref: item.ref, ...item.data() });
          });
        } catch (error) {
          console.error("Error fetching transactions by phone:", error);
        }
      })
    );

    return Array.from(docsById.values()).sort(
      (a, b) => parseTxnDateTime(b) - parseTxnDateTime(a)
    );
  }, []);

  useEffect(() => {
    loadTransactionsForDate();
  }, [loadTransactionsForDate]);
  const [foodNoTax, setFoodNoTax] = useState(false);
  const [freeEntry, setFreeEntry] = useState(false);
  const [customerNote, setCustomerNote] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteLoadedForPhone, setNoteLoadedForPhone] = useState("");

  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(() => getTabFromSearch(location.search));
  const [eventsOpen, setEventsOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [privateEventPrefill, setPrivateEventPrefill] = useState(null);
  const [invoicePrefillRequest, setInvoicePrefillRequest] = useState(null);
  const [depositPrefillRequest, setDepositPrefillRequest] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const calendarBookingCacheRef = useRef(new Map());
  const calendarBookingFetchRef = useRef(new Map());

  const [children, setChildren] = useState([
    {
      parentName: "",
      phoneNumber: "",
      playtimeHours: 3,
      playtimeCost: 30,
      paymentMethod: "",
      siblingGroup: "",
      customPriceEnabled: false, // NEW
      customPrice: null,
    },
  ]);
  const [foodOrders, setFoodOrders] = useState([]);
  const [foodOptions, setFoodOptions] = useState([]);
  useEffect(() => {
    const fetchFoodOptions = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "foodItems"));
        const fetchedOptions = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const sortedOptions = fetchedOptions.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setFoodOptions(sortedOptions);
      } catch (error) {
        console.error("Error fetching food options:", error);
      }
    };
    fetchFoodOptions();
  }, []);

  const [discountType, setDiscountType] = useState("");
  // const [customPrice, setCustomPrice] = useState(null);
  const [foodOrderPhone, setFoodOrderPhone] = useState("");
  const [foodOrderName, setFoodOrderName] = useState("");
  const [foodOrderPayment, setFoodOrderPayment] = useState("");
  const [foodQuickQuery, setFoodQuickQuery] = useState("");
  const [newFoodName, setNewFoodName] = useState("");
  const [newFoodPrice, setNewFoodPrice] = useState("");
  const TAX_RATE = 0.095;

  const handleAddFoodItem = async () => {
    try {
      await addDoc(collection(db, "foodItems"), {
        name: newFoodName,
        price: newFoodPrice,
      });
      setNewFoodName("");
      setNewFoodPrice("");
      const updatedSnapshot = await getDocs(collection(db, "foodItems"));
      setFoodOptions(
        updatedSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      );
    } catch (error) {
      console.error("Error adding food item:", error);
    }
  };
  const handleDeleteFoodItem = async (id) => {
    try {
      await deleteDoc(doc(db, "foodItems", id));
      setFoodOptions((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Error deleting food item:", error);
    }
  };
  const handleUpdateFoodPrice = async (id, newPrice) => {
    try {
      await updateDoc(doc(db, "foodItems", id), {
        price: newPrice,
      });
      setFoodOptions((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, price: newPrice } : item
        )
      );
    } catch (error) {
      console.error("Error updating food price:", error);
    }
  };
  // const calculateFoodTotal = () => {
  //   const subtotal = foodOrders.reduce(
  //     (total, order) => total + order.total,
  //     0
  //   );
  //   let taxAmount = subtotal * TAX_RATE;

  //   if (discountType === "noTax") {
  //     taxAmount = 0;
  //   }

  //   let totalWithFees = subtotal + taxAmount;

  //   if (foodOrderPayment === "Card") {
  //     totalWithFees *= 1 + CARD_FEE_RATE;
  //   }

  //   if (discountType === "customPrice" && customPrice !== null) {
  //     totalWithFees = customPrice;
  //   }

  //   return totalWithFees;
  // };
  const calculateFoodTotal = () => {
    const subtotal = foodOrders.reduce(
      (total, order) => total + order.total,
      0
    );
    const taxAmount = foodNoTax ? 0 : subtotal * TAX_RATE;

    let totalWithFees = subtotal + taxAmount;
    if (foodOrderPayment === "Card") {
      totalWithFees *= 1 + CARD_FEE_RATE;
    }
    return totalWithFees;
  };

  const setHomeRouteState = useCallback(
    (tab, eventId = "") => {
      const params = new URLSearchParams(location.search);

      if (tab === "home") {
        params.delete("tab");
      } else {
        params.set("tab", tab);
      }

      if (DOCUMENT_TABS.has(tab) && eventId) {
        params.set("eventId", eventId);
      } else {
        params.delete("eventId");
      }

      const nextSearch = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace: true }
      );
    },
    [location.pathname, location.search, navigate]
  );

  const ensureCalendarBookingLoaded = useCallback(async (eventId) => {
    if (!eventId) return null;

    if (calendarBookingCacheRef.current.has(eventId)) {
      return calendarBookingCacheRef.current.get(eventId);
    }

    if (calendarBookingFetchRef.current.has(eventId)) {
      return calendarBookingFetchRef.current.get(eventId);
    }

    // Keep the original collection name for compatibility with existing data.
    const loadPromise = getDoc(doc(db, "privateEvents", eventId))
      .then((snapshot) => {
        if (!snapshot.exists()) return null;
        const payload = { id: snapshot.id, ...snapshot.data() };
        calendarBookingCacheRef.current.set(eventId, payload);
        return payload;
      })
      .finally(() => {
        calendarBookingFetchRef.current.delete(eventId);
      });

    calendarBookingFetchRef.current.set(eventId, loadPromise);
    return loadPromise;
  }, []);

  const createDocumentRequest = useCallback((tab, calendarBooking) => {
    const data =
      tab === "invoiceGenerator"
        ? mapCalendarBookingToInvoicePrefill(calendarBooking)
        : mapCalendarBookingToDepositPrefill(calendarBooking);

    return {
      eventId: calendarBooking?.id || "",
      requestKey: `${tab}:${calendarBooking?.id || "manual"}:${Date.now()}`,
      data,
    };
  }, []);

  const setDocumentResetRequest = useCallback((tab) => {
    const request = {
      eventId: "",
      requestKey: `${tab}:manual:${Date.now()}`,
      data: null,
    };

    if (tab === "invoiceGenerator") {
      setInvoicePrefillRequest(request);
      return;
    }

    setDepositPrefillRequest(request);
  }, []);

  const openTab = useCallback(
    (tab, options = {}) => {
      const eventId = options.eventId || "";

      if (tab === "invoiceGenerator" && !eventId) {
        setDocumentResetRequest(tab);
      }

      if (tab === "depositConfirmationGenerator" && !eventId) {
        setDocumentResetRequest(tab);
      }

      setActiveTab(tab);
      setHomeRouteState(tab, eventId);
      setMenuOpen(false);
    },
    [setDocumentResetRequest, setHomeRouteState]
  );

  const openDocumentFromCalendarBooking = useCallback(
    (tab, calendarBooking) => {
      if (!calendarBooking?.id) return;
      calendarBookingCacheRef.current.set(calendarBooking.id, calendarBooking);
      const request = createDocumentRequest(tab, calendarBooking);

      if (tab === "invoiceGenerator") {
        setInvoicePrefillRequest(request);
      } else {
        setDepositPrefillRequest(request);
      }

      openTab(tab, { eventId: calendarBooking.id });
    },
    [createDocumentRequest, openTab]
  );

  const handleGenerateInvoiceFromEvent = useCallback(
    (calendarBooking) => {
      openDocumentFromCalendarBooking("invoiceGenerator", calendarBooking);
    },
    [openDocumentFromCalendarBooking]
  );

  const handleGenerateDepositConfirmationFromEvent = useCallback(
    (calendarBooking) => {
      openDocumentFromCalendarBooking(
        "depositConfirmationGenerator",
        calendarBooking
      );
    },
    [openDocumentFromCalendarBooking]
  );

  useEffect(() => {
    const searchTab = getTabFromSearch(location.search);
    const eventId = getDocumentEventIdFromSearch(location.search);

    if (searchTab !== activeTab) {
      setActiveTab(searchTab);
      return;
    }

    if (!DOCUMENT_TABS.has(searchTab) || !eventId) return;

    const existingRequest =
      searchTab === "invoiceGenerator"
        ? invoicePrefillRequest
        : depositPrefillRequest;

    if (existingRequest?.eventId === eventId) return;

    let ignore = false;

    ensureCalendarBookingLoaded(eventId)
      .then((calendarBooking) => {
        if (ignore || !calendarBooking) return;
        const request = createDocumentRequest(searchTab, calendarBooking);
        if (searchTab === "invoiceGenerator") {
          setInvoicePrefillRequest(request);
        } else {
          setDepositPrefillRequest(request);
        }
      })
      .catch((error) => {
        console.error("Unable to load calendar booking for document prefill:", error);
      });

    return () => {
      ignore = true;
    };
  }, [
    activeTab,
    createDocumentRequest,
    depositPrefillRequest,
    ensureCalendarBookingLoaded,
    invoicePrefillRequest,
    location.search,
  ]);

  useEffect(() => {
    if (isAdmin) return;
    const restricted = new Set([
      "monthlyReport",
      "dailyAnalysis",
      "foodReport",
      "businessTrends",
      "inventory",
      "holidayInsights",
      "expenses",
      "users",
    ]);
    if (restricted.has(activeTab)) openTab("home");
  }, [isAdmin, activeTab, openTab]);

  const eventsTabs = [
    "privateEvents",
    "privateEventNotes",
    "tracker",
    "invoiceGenerator",
    "depositConfirmationGenerator",
  ];
  const reportTabs = [
    "monthlyReport",
    "dailyAnalysis",
    "foodReport",
    "businessTrends",
    "holidayInsights",
    "expenses",
    "inventory",
  ];
  const isEventsActive = eventsTabs.includes(activeTab);
  const isReportsActive = reportTabs.includes(activeTab);

  useEffect(() => {
    if (isEventsActive) setEventsOpen(true);
    if (isReportsActive) setReportsOpen(true);
  }, [isEventsActive, isReportsActive]);

  const handleLogout = () => {
    clearCurrentUser();
    setCurrentUser(null);
    navigate("/");
  };

  const handleBookPrivateEventLead = (lead) => {
    setPrivateEventPrefill({
      leadId: lead?.id || null,
      firstName: lead?.firstName || "",
      lastName: lead?.lastName || "",
      phoneNumber: lead?.phoneNumber || "",
      email: lead?.email || "",
      message: lead?.details || "",
      eventDate: lead?.eventDate || "",
      startTime: lead?.startTime || "",
      endTime: lead?.endTime || "",
      packageType: lead?.packageType || "",
    });
    openTab("privateEvents");
  };
  // const handleInputChange = (index, field, value) => {
  //   const updatedChildren = [...children];
  //   updatedChildren[index][field] = value;
  //   setChildren(updatedChildren);
  // };
  const handleInputChange = (index, field, value) => {
    const updated = [...children];
    updated[index][field] = value;

    // If the field affects pricing (siblingGroup), or to keep it simple, always recalc:
    setChildren(recalcSiblingPrices(updated));
  };

  const handleAddChild = () => {
    const firstChild = children[0];
    setChildren([
      ...children,
      {
        parentName: firstChild ? firstChild.parentName : "",
        phoneNumber: firstChild ? firstChild.phoneNumber : "",
        playtimeHours: 3,
        playtimeCost: 30,
        paymentMethod: "",
        siblingGroup: "",
        customPriceEnabled: false, // NEW
        customPrice: null,
      },
    ]);
  };
  // const handleRemoveChild = (index) => {
  //   const updatedChildren = [...children];
  //   updatedChildren.splice(index, 1);
  //   setChildren(updatedChildren);
  // };
  const handleRemoveChild = (index) => {
    const updated = [...children];
    updated.splice(index, 1);
    setChildren(recalcSiblingPrices(updated));
  };
  const recalcSiblingPrices = (list) => {
    const groupCounts = {};
    return list.map((child, idx) => {
      // Sibling base (30 / 25 / 20)
      const g =
        child.siblingGroup && child.siblingGroup.trim() !== ""
          ? child.siblingGroup.trim().toUpperCase()
          : `__solo_${idx}`;

      const count = (groupCounts[g] || 0) + 1;
      groupCounts[g] = count;

      let base = 30;
      if (count === 2) base = 25;
      else if (count >= 3) base = 20;

      // Use per-child custom price ONLY if enabled & set
      const useCustom =
        child.customPriceEnabled &&
        child.customPrice !== null &&
        !Number.isNaN(child.customPrice);

      const playtimeCost = useCustom ? child.customPrice : base;
      return { ...child, playtimeCost };
    });
  };

  // For Expenses tab filters
  const [expensesMonth, setExpensesMonth] = useState(new Date().getMonth() + 1);
  const [expensesYear, setExpensesYear] = useState(new Date().getFullYear());
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // const recalcSiblingPrices = (list) => {
  //   const groupCounts = {};
  //   return list.map((child, idx) => {
  //     // Treat empty group as unique (no discount)
  //     const g =
  //       child.siblingGroup && child.siblingGroup.trim() !== ""
  //         ? child.siblingGroup.trim().toUpperCase()
  //         : `__solo_${idx}`; // unique per child → always $30
  //
  //     const count = (groupCounts[g] || 0) + 1;
  //     groupCounts[g] = count;
  //
  //     let price = 30;
  //     if (count === 2) price = 25;
  //     else if (count >= 3) price = 20;
  //
  //     return { ...child, playtimeCost: price };
  //   });
  // };

  // const handleSiblingDiscount = (index) => {
  //   const updatedChildren = [...children];
  //   const childCount = index + 1;
  //
  //   if (childCount === 2) {
  //     updatedChildren[index].playtimeCost = 25;
  //   } else if (childCount === 3) {
  //     updatedChildren[index].playtimeCost = 20;
  //   }
  //   setChildren(updatedChildren);
  // };
  const handleAddFoodOrder = () => {
    setFoodOrders([...foodOrders, { food: "", quantity: 1, total: 0 }]);
  };
  const handleRemoveFoodOrder = (index) => {
    const updatedFoodOrders = [...foodOrders];
    updatedFoodOrders.splice(index, 1);
    setFoodOrders(updatedFoodOrders);
  };

  const handleQuickAddFood = (foodItem) => {
    setFoodOrders((prev) => upsertFoodOrderLine(prev, foodItem, 1));
  };

  const handleAdjustFoodQuantity = (index, delta) => {
    const item = foodOrders[index];
    if (!item) return;

    const currentQuantity = Number(item.quantity || 0);
    const nextQuantity = currentQuantity + delta;
    if (nextQuantity <= 0) {
      handleRemoveFoodOrder(index);
      return;
    }
    handleFoodOrderChange(index, "quantity", nextQuantity);
  };
  const handleFoodOrderChange = (index, field, value) => {
    const updatedFoodOrders = [...foodOrders];

    if (field === "quantity") {
      const asNumber = Number(value);
      updatedFoodOrders[index][field] = Number.isFinite(asNumber)
        ? Math.max(1, asNumber)
        : 1;
    } else {
      updatedFoodOrders[index][field] = value;
    }

    if (field === "food" || field === "quantity") {
      const selectedFood = foodOptions.find(
        (option) => option.name === updatedFoodOrders[index].food
      );
      const quantity = Number(updatedFoodOrders[index].quantity || 0);

      if (selectedFood) {
        updatedFoodOrders[index].total = Number(selectedFood.price || 0) * quantity;
      } else {
        updatedFoodOrders[index].total = 0;
      }
    }

    setFoodOrders(updatedFoodOrders);
  };
  // const calculateTotalAmount = () => {
  //   const playtimeTotal = children.reduce(
  //     (total, child) => total + child.playtimeCost,
  //     0
  //   );
  //   const foodTotal = foodOrders.reduce(
  //     (total, order) => total + order.total,
  //     0
  //   );
  //
  //   const subtotal = playtimeTotal + foodTotal;
  //   let taxAmount = subtotal * TAX_RATE;
  //
  //   if (discountType === "noTax") {
  //     taxAmount = 0;
  //   }
  //
  //   let totalWithFees = subtotal + taxAmount;
  //
  //   const isCardPayment = children.some(
  //     (child) => child.paymentMethod === "Card"
  //   );
  //   if (isCardPayment) {
  //     totalWithFees *= 1 + CARD_FEE_RATE;
  //   }
  //
  //   if (discountType === "customPrice" && customPrice !== null) {
  //     totalWithFees = customPrice;
  //   }
  //
  //   return totalWithFees;
  // };
  const calculateTotalAmount = () => {
    // Play subtotal before any free-entry override
    const playSubtotalBase = children.reduce(
      (total, child) => total + child.playtimeCost,
      0
    );
    // If Free Entry, playSubtotal becomes 0
    const playSubtotal = freeEntry ? 0 : playSubtotalBase;

    // Food subtotal (unchanged)
    const foodSubtotal = foodOrders.reduce(
      (total, order) => total + order.total,
      0
    );

    // Taxes: play tax removed if noTax OR freeEntry
    const playTax =
      discountType === "noTax" || freeEntry ? 0 : playSubtotal * TAX_RATE;

    // Food tax removed only if you checked foodNoTax
    const foodTax = foodNoTax ? 0 : foodSubtotal * TAX_RATE;

    let total = playSubtotal + foodSubtotal + playTax + foodTax;

    // Card fee applies only to the combined non-zero total
    const anyCard =
      children.some((c) => c.paymentMethod === "Card") ||
      foodOrderPayment === "Card";
    if (anyCard) {
      total *= 1 + CARD_FEE_RATE;
    }
    return total;
  };

  const handleTransactionSubmit = async (e) => {
    e.preventDefault();

    // If it's NOT free entry, still require a payment method per child
    if (!freeEntry && children.some((child) => !child.paymentMethod)) {
      alert("Please select a payment method for all children.");
      return;
    }

    // Play subtotal (before tax/fees); if free entry ⇒ 0
    const rawPlaySubtotal = children.reduce(
      (sum, child) => sum + child.playtimeCost,
      0
    );
    const totalBeforeTax = freeEntry ? 0 : rawPlaySubtotal;

    // Tax for play: remove if free entry OR noTax checked
    let taxAmount =
      freeEntry || discountType === "noTax" ? 0 : totalBeforeTax * TAX_RATE;

    // Card/Cash splits for play only (food is handled in Food tab)
    let totalCardAmount = 0;
    let totalCashAmount = 0;

    if (!freeEntry) {
      const anyCard = children.some((child) => child.paymentMethod === "Card");
      const anyCash = children.some((child) => child.paymentMethod === "Cash");

      if (anyCard) {
        totalCardAmount = (totalBeforeTax + taxAmount) * (1 + CARD_FEE_RATE);
      }
      if (anyCash) {
        totalCashAmount = totalBeforeTax + taxAmount;
      }
    }

    const totalAmount = totalCardAmount + totalCashAmount;

    const formattedPayMethod = freeEntry
      ? "Free"
      : Array.from(new Set(children.map((child) => child.paymentMethod)))
          .filter(Boolean)
          .join(" & ");

    // Note: DiscountApplied: keep your existing noTax and add 'freeEntry' if used
    const discountApplied = [
      discountType === "noTax" ? "noTax" : null,
      freeEntry ? "freeEntry" : null,
    ]
      .filter(Boolean)
      .join(" & ");

    const transactionData = {
      PName: children[0]?.parentName || "Unknown",
      PhoneNum: children[0]?.phoneNumber || "N/A",
      CustomerNote: customerNote || "",
      PayMethod: formattedPayMethod,
      Date: new Date().toLocaleDateString(),
      Time: new Date().toLocaleTimeString(),
      Month: new Date().getMonth() + 1,
      Year: new Date().getFullYear(),
      PlayCost: totalBeforeTax, // play only
      TotalBeforeTax: totalBeforeTax, // play only (food adds later in food flow)
      TaxAmount: taxAmount, // play tax only
      TotalCashAmount: totalCashAmount,
      TotalCardAmount: totalCardAmount,
      TotalAmount: totalAmount, // play total only
      KidCount: children.length,
      DiscountApplied: discountApplied,
      FreeEntry: freeEntry, // helpful flag for reports
      UserId: currentUser?.id || auth.currentUser?.uid || "guest",
      RecordedByUserId: currentUser?.id || "",
      RecordedByUsername: currentUser?.username || "",
      RecordedByRole: currentUser?.role || "",
    };

    try {
      const docRef = await addDoc(
        collection(db, "transactions"),
        transactionData
      );
      console.log("Transaction saved with ID:", docRef.id);
      setTransactions((prev) => [
        ...prev,
        { id: docRef.id, ...transactionData },
      ]);

      setChildren([
        {
          parentName: "",
          phoneNumber: "",
          playtimeHours: 3,
          playtimeCost: 30,
          paymentMethod: "",
          siblingGroup: "",
          customPriceEnabled: false,
          customPrice: null,
        },
      ]);
      setDiscountType("");
      setFreeEntry(false); // reset the new checkbox
      setCustomerNote("");
      setNoteLoadedForPhone("");
    } catch (err) {
      console.error("Error saving transaction:", err);
    }
  };

  const loadCustomerNote = async (phoneRaw) => {
    const phoneId = normalizePhone(phoneRaw);
    if (phoneId.length < 10) {
      setCustomerNote("");
      setNoteLoadedForPhone("");
      return;
    }
    if (phoneId === noteLoadedForPhone) return;

    try {
      const matches = await queryTransactionsByPhone(phoneRaw, 25);

      setCustomerNote(matches[0]?.CustomerNote || "");
      setNoteLoadedForPhone(phoneId);
    } catch (error) {
      console.error("Error fetching customer note:", error);
    }
  };

  const saveCustomerNote = async () => {
    const phoneRaw = children[0]?.phoneNumber || "";
    const phoneId = normalizePhone(phoneRaw);
    if (phoneId.length < 10) {
      alert("Enter a valid phone number first.");
      return;
    }
    setNoteBusy(true);
    try {
      const matchingDocs = await queryTransactionsByPhone(phoneRaw, 100);

      if (matchingDocs.length === 0) {
        alert(
          "No existing transactions found for this phone yet. The note will be saved on the next transaction you add."
        );
      } else {
        const batch = writeBatch(db);
        matchingDocs.forEach((d) => {
          batch.update(doc(db, "transactions", d.id), {
            CustomerNote: customerNote || "",
          });
        });
        await batch.commit();

        setTransactions((prev) =>
          (prev || []).map((t) =>
            normalizePhone(t.PhoneNum) === phoneId
              ? { ...t, CustomerNote: customerNote || "" }
              : t
          )
        );
      }
      setNoteLoadedForPhone(phoneId);
    } catch (error) {
      console.error("Error saving customer note:", error);
      alert("Failed to save note. See console for details.");
    } finally {
      setNoteBusy(false);
    }
  };

  const handlePhoneNumberChange = async (index, value) => {
    const updatedChildren = [...children];
    updatedChildren[index].phoneNumber = value;

    if (index === 0) {
      loadCustomerNote(value);
    }

    if (value.length >= 10) {
      try {
        const matches = await queryTransactionsByPhone(value, 10);
        const existingRecord = matches[0];
        if (existingRecord) {
          updatedChildren[index].parentName = existingRecord.PName;
        }
      } catch (error) {
        console.error("Error fetching existing user:", error);
      }
    }
    setChildren(updatedChildren);
  };
  const handleFoodOrderPhoneChange = async (value) => {
    setFoodOrderPhone(value);

    if (value.length >= 10) {
      try {
        const matches = await queryTransactionsByPhone(value, 10);
        const existingRecord = matches[0];
        if (existingRecord) {
          setFoodOrderName(existingRecord.PName);
        } else {
          setFoodOrderName("");
        }
      } catch (error) {
        console.error("Error fetching customer:", error);
      }
    } else {
      setFoodOrderName("");
    }
  };
  const handleFoodTransactionSubmit = async (e) => {
    e.preventDefault();

    if (!foodOrderPayment) {
      alert("Please select a payment method for the food order.");
      return;
    }

    const cleanedFoodOrders = sanitizeFoodOrders(foodOrders);
    if (cleanedFoodOrders.length === 0) {
      alert("Please add at least 1 food item to the order.");
      return;
    }

    const now = new Date();
    const todayDate = now.toLocaleDateString();
    const UserId = auth.currentUser?.uid || "guest";

    try {
      const possiblePhones = Array.from(
        new Set([foodOrderPhone, normalizePhone(foodOrderPhone)].filter(Boolean))
      );
      const todayMatches = [];
      await Promise.all(
        possiblePhones.map(async (phone) => {
          const snap = await getDocs(
            query(
              collection(db, "transactions"),
              where("Date", "==", todayDate),
              where("PhoneNum", "==", phone),
              limit(3)
            )
          );
          snap.docs.forEach((item) =>
            todayMatches.push({ id: item.id, ...item.data() })
          );
        })
      );
      const existingRecord = todayMatches[0] || null;
      let existingTransaction = existingRecord || null;
      let transactionId = existingRecord?.id || null;
      const submittedFoodOrders = createFoodOrderBatch(
        cleanedFoodOrders,
        doc(collection(db, "transactions")).id,
        now
      );
      const newFoodTotalBeforeTax = cleanedFoodOrders.reduce(
        (total, order) => total + order.total,
        0
      );
      const newTaxAmount = foodNoTax ? 0 : newFoodTotalBeforeTax * TAX_RATE;

      let newCardAmount = 0;
      let newCashAmount = 0;

      if (foodOrderPayment === "Card") {
        newCardAmount =
          (newFoodTotalBeforeTax + newTaxAmount) * (1 + CARD_FEE_RATE);
      } else {
        newCashAmount = newFoodTotalBeforeTax + newTaxAmount;
      }

      if (existingTransaction) {
        const updatedFoodOrders = existingTransaction.FoodOrder
          ? [...existingTransaction.FoodOrder, ...submittedFoodOrders]
          : [...submittedFoodOrders];
        const updatedTotalBeforeTax =
          (existingTransaction.TotalBeforeTax || 0) + newFoodTotalBeforeTax;
        const updatedTaxAmount =
          (existingTransaction.TaxAmount || 0) + newTaxAmount;
        const updatedTotalCashAmount =
          (existingTransaction.TotalCashAmount || 0) + newCashAmount;
        const updatedTotalCardAmount =
          (existingTransaction.TotalCardAmount || 0) + newCardAmount;

        const updatedTotalAmount =
          (existingTransaction.TotalAmount || 0) +
          newFoodTotalBeforeTax +
          newTaxAmount;

        let updatedPayMethod = existingTransaction.PayMethod || "";
        const paymentMethods = new Set(updatedPayMethod.split(" & "));
        paymentMethods.add(foodOrderPayment);
        updatedPayMethod = Array.from(paymentMethods).join(" & ");

        await updateDoc(doc(db, "transactions", transactionId), {
          FoodOrder: updatedFoodOrders,
          TotalBeforeTax: updatedTotalBeforeTax,
          TaxAmount: updatedTaxAmount,
          TotalCashAmount: updatedTotalCashAmount,
          TotalCardAmount: updatedTotalCardAmount,
          TotalAmount: updatedTotalAmount,
          PlayCost: existingTransaction.PlayCost || 0,
          PayMethod: updatedPayMethod,
          UpdatedByUserId: currentUser?.id || "",
          UpdatedByUsername: currentUser?.username || "",
          UpdatedByRole: currentUser?.role || "",
        });
      } else {
        const transactionData = {
          PName: foodOrderName || "Unknown",
          PhoneNum: foodOrderPhone || "N/A",
          PayMethod: foodOrderPayment,
          Date: todayDate,
          Time: now.toLocaleTimeString(),
          Month: now.getMonth() + 1,
          Year: now.getFullYear(),
          FoodOrder: submittedFoodOrders,
          PlayCost: 0,
          TotalBeforeTax: newFoodTotalBeforeTax,
          TaxAmount: newTaxAmount,
          TotalCashAmount: newCashAmount,
          TotalCardAmount: newCardAmount,
          TotalAmount: newCashAmount + newCardAmount,
          UserId: currentUser?.id || UserId,
          RecordedByUserId: currentUser?.id || "",
          RecordedByUsername: currentUser?.username || "",
          RecordedByRole: currentUser?.role || "",
        };
        const docRef = await addDoc(
          collection(db, "transactions"),
          transactionData
        );
        console.log("New transaction created with ID:", docRef.id);
        setTransactions((prev) => [
          ...prev,
          { id: docRef.id, ...transactionData },
        ]);
      }

      await loadTransactionsForDate(todayDate);

      setFoodOrders([]);
      setFoodOrderPhone("");
      setFoodOrderName("");
      setFoodOrderPayment("");
    } catch (err) {
      console.error("Error processing food order:", err);
    }
  };
  return (
    <div className={`wp-shell tab-${activeTab} ${menuOpen ? "menu-open" : ""}`}>
      {menuOpen && (
        <button
          type="button"
          className="wp-backdrop"
          aria-label="Close menu"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <aside className="wp-sidebar" aria-label="Primary navigation">
        <div className="wp-sidebar-header">
          <button
            type="button"
            className="wp-sidebar-toggle"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <FaBars />
          </button>
          <div className="wp-sidebar-brand">Kidsystem</div>
        </div>

        <nav className="wp-nav">
          <button
            type="button"
            className={`wp-nav-item ${activeTab === "home" ? "active" : ""}`}
            onClick={() => openTab("home")}
          >
            <FaHome className="wp-nav-icon" />
            <span className="wp-nav-label">Home</span>
          </button>

          <button
            type="button"
            className={`wp-nav-item ${
              activeTab === "foodOrders" ? "active" : ""
            }`}
            onClick={() => openTab("foodOrders")}
          >
            <FaUtensils className="wp-nav-icon" />
            <span className="wp-nav-label">Food Orders</span>
          </button>

          <button
            type="button"
            className={`wp-nav-item ${
              activeTab === "dailyTransactions" ? "active" : ""
            }`}
            onClick={() => openTab("dailyTransactions")}
          >
            <FaReceipt className="wp-nav-icon" />
            <span className="wp-nav-label">Daily Transactions</span>
          </button>

          <div className={`wp-nav-group ${eventsOpen ? "open" : ""}`}>
            <button
              type="button"
              className={`wp-nav-group-header ${
                isEventsActive ? "active" : ""
              }`}
              aria-expanded={eventsOpen}
              onClick={() => setEventsOpen((v) => !v)}
            >
              <span className="wp-nav-group-left">
                <FaCalendarAlt className="wp-nav-icon" />
                <span className="wp-nav-label">Events</span>
              </span>
              {eventsOpen ? (
                <FaChevronDown className="wp-nav-chevron" />
              ) : (
                <FaChevronRight className="wp-nav-chevron" />
              )}
            </button>

            <div className="wp-nav-submenu">
              <button
                type="button"
                className={`wp-nav-subitem ${
                  activeTab === "privateEvents" ? "active" : ""
                }`}
                onClick={() => openTab("privateEvents")}
              >
                <span className="wp-nav-label">Event Calendar</span>
              </button>

              <button
                type="button"
                className={`wp-nav-subitem ${
                  activeTab === "privateEventNotes" ? "active" : ""
                }`}
                onClick={() => openTab("privateEventNotes")}
              >
                <span className="wp-nav-label">Event Notes</span>
              </button>

              <button
                type="button"
                className={`wp-nav-subitem ${
                  activeTab === "tracker" ? "active" : ""
                }`}
                onClick={() => openTab("tracker")}
              >
                <FaClipboardList className="wp-nav-subicon" />
                <span className="wp-nav-label">Tracker</span>
              </button>

              <button
                type="button"
                className={`wp-nav-subitem ${
                  activeTab === "invoiceGenerator" ? "active" : ""
                }`}
                onClick={() => openTab("invoiceGenerator")}
              >
                <FaFileInvoiceDollar className="wp-nav-subicon" />
                <span className="wp-nav-label">Invoice</span>
              </button>
              <button
                type="button"
                className={`wp-nav-subitem ${
                  activeTab === "depositConfirmationGenerator" ? "active" : ""
                }`}
                onClick={() => openTab("depositConfirmationGenerator")}
              >
                <FaReceipt className="wp-nav-subicon" />
                <span className="wp-nav-label">Deposit</span>
              </button>
            </div>
          </div>

          {isAdmin && (
            <div className={`wp-nav-group ${reportsOpen ? "open" : ""}`}>
              <button
                type="button"
                className={`wp-nav-group-header ${
                  isReportsActive ? "active" : ""
                }`}
                aria-expanded={reportsOpen}
                onClick={() => setReportsOpen((v) => !v)}
              >
                <span className="wp-nav-group-left">
                  <FaChartBar className="wp-nav-icon" />
                  <span className="wp-nav-label">Reports</span>
                </span>
                {reportsOpen ? (
                  <FaChevronDown className="wp-nav-chevron" />
                ) : (
                  <FaChevronRight className="wp-nav-chevron" />
                )}
              </button>

              <div className="wp-nav-submenu">
                <button
                  type="button"
                  className={`wp-nav-subitem ${
                    activeTab === "monthlyReport" ? "active" : ""
                  }`}
                  onClick={() => openTab("monthlyReport")}
                >
                  <span className="wp-nav-label">Monthly Report</span>
                </button>

                <button
                  type="button"
                  className={`wp-nav-subitem ${
                    activeTab === "dailyAnalysis" ? "active" : ""
                  }`}
                  onClick={() => openTab("dailyAnalysis")}
                >
                  <span className="wp-nav-label">Daily Analysis</span>
                </button>

                <button
                  type="button"
                  className={`wp-nav-subitem ${
                    activeTab === "foodReport" ? "active" : ""
                  }`}
                  onClick={() => openTab("foodReport")}
                >
                  <span className="wp-nav-label">Food Report</span>
                </button>

                <button
                  type="button"
                  className={`wp-nav-subitem ${
                    activeTab === "businessTrends" ? "active" : ""
                  }`}
                  onClick={() => openTab("businessTrends")}
                >
                  <span className="wp-nav-label">Progression Line</span>
                </button>

                <button
                  type="button"
                  className={`wp-nav-subitem ${
                    activeTab === "holidayInsights" ? "active" : ""
                  }`}
                  onClick={() => openTab("holidayInsights")}
                >
                  <span className="wp-nav-label">Holidays</span>
                </button>

                <button
                  type="button"
                  className={`wp-nav-subitem ${
                    activeTab === "expenses" ? "active" : ""
                  }`}
                  onClick={() => openTab("expenses")}
                >
                  <span className="wp-nav-label">Expenses</span>
                </button>

                <button
                  type="button"
                  className={`wp-nav-subitem ${
                    activeTab === "inventory" ? "active" : ""
                  }`}
                  onClick={() => openTab("inventory")}
                >
                  <span className="wp-nav-label">Manage Inventory</span>
                </button>
              </div>
            </div>
          )}

          <button
            type="button"
            className={`wp-nav-item ${
              activeTab === "customerInfo" ? "active" : ""
            }`}
            onClick={() => openTab("customerInfo")}
          >
            <FaUserFriends className="wp-nav-icon" />
            <span className="wp-nav-label">Customer Info</span>
          </button>

          {isAdmin && (
            <button
              type="button"
              className={`wp-nav-item ${activeTab === "users" ? "active" : ""}`}
              onClick={() => openTab("users")}
            >
              <FaUsers className="wp-nav-icon" />
              <span className="wp-nav-label">Users</span>
            </button>
          )}
        </nav>
      </aside>

      <main className="wp-main">
        <div className="top-bar">
          <button
            type="button"
            className="wp-mobile-toggle"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <FaBars />
          </button>
          <div className="top-bar-user">
            {currentUser?.username ? `Signed in as ${currentUser.username}` : ""}
          </div>
          <button
            className="top-bar-logout"
            onClick={handleLogout}
            type="button"
          >
            Logout
          </button>
        </div>

        <div className="homepage">
          <div className="tab-content">
        {activeTab === "home" && (
          <div className="home-tab">
            <h2>Process Transaction</h2>
            <form onSubmit={handleTransactionSubmit}>
              {children.map((child, childIndex) => (
                <div key={childIndex} className="child-section">
                  <div className="child-header">
                    <h3>Child {childIndex + 1}</h3>
                    <button
                      type="button"
                      className="remove-child-button"
                      onClick={() => handleRemoveChild(childIndex)}
                    >
                      ✖
                    </button>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor={`phoneNumber-${childIndex}`}>
                        Phone Number:
                      </label>
                      <input
                        type="tel"
                        id={`phoneNumber-${childIndex}`}
                        name="phoneNumber"
                        value={child.phoneNumber}
                        onChange={(e) =>
                          handlePhoneNumberChange(childIndex, e.target.value)
                        }
                        placeholder="Enter phone number"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor={`parentName-${childIndex}`}>
                        Parent Name:
                      </label>
                      <input
                        type="text"
                        id={`parentName-${childIndex}`}
                        name="parentName"
                        value={child.parentName}
                        onChange={(e) =>
                          handleInputChange(
                            childIndex,
                            "parentName",
                            e.target.value
                          )
                        }
                        placeholder="Auto-filled if registered"
                        required
                      />
                    </div>
                  </div>

                  {childIndex === 0 && (
                    <div className="form-group">
                      <label htmlFor="customerNote">Customer Note:</label>
                      <textarea
                        id="customerNote"
                        value={customerNote}
                        onChange={(e) => setCustomerNote(e.target.value)}
                        placeholder="Add a note for this customer (saved for future visits)"
                        rows={3}
                      />
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          alignItems: "center",
                          marginTop: 10,
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          type="button"
                          className="add-btn"
                          onClick={saveCustomerNote}
                          disabled={noteBusy}
                        >
                          {noteBusy ? "Saving..." : "Save Note"}
                        </button>
                        {noteLoadedForPhone && (
                          <small style={{ color: "var(--muted)" }}>
                            Loaded for {children[0]?.phoneNumber || ""}
                          </small>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Playtime:</label>
                    <p>{child.playtimeHours} hours</p>
                    <p>Cost: ${child.playtimeCost}</p>
                  </div>
                  {/* {childIndex > 0 && childIndex < 3 && (
                    <button
                      type="button"
                      className="sibling-discount-button"
                      onClick={() => handleSiblingDiscount(childIndex)}
                    >
                      Apply Sibling Discount
                    </button>
                  )} */}
                  <div className="form-group">
                    <label htmlFor={`siblingGroup-${childIndex}`}>
                      Sibling Group:
                    </label>
                    <select
                      id={`siblingGroup-${childIndex}`}
                      value={child.siblingGroup}
                      onChange={(e) =>
                        handleInputChange(
                          childIndex,
                          "siblingGroup",
                          e.target.value
                        )
                      }
                    >
                      <option value="">None</option>
                      <option value="A">Group A</option>
                      <option value="B">Group B</option>
                      <option value="C">Group C</option>
                      <option value="D">Group D</option>
                      <option value="E">Group E</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor={`paymentMethod-${childIndex}`}>
                      Payment Method:
                    </label>
                      <select
                        id={`paymentMethod-${childIndex}`}
                        name="paymentMethod"
                        value={child.paymentMethod}
                        onChange={(e) =>
                          handleInputChange(
                            childIndex,
                            "paymentMethod",
                            e.target.value
                          )
                        }
                      required={!freeEntry}
                      disabled={freeEntry}
                    >
                      <option value="">Select Payment Method</option>
                      <option value="Cash">Cash</option>
                      <option value="Card">Card</option>
                    </select>
                    {/* <div className="form-group">
                      <label>Discount Type:</label>
                      <select
                        name="discountType"
                        value={discountType}
                        onChange={(e) => setDiscountType(e.target.value)}
                      >
                        <option value="">No Discount</option>
                        <option value="noTax">Remove Tax</option>
                        <option value="customPrice">Custom Price</option>
                      </select>
                    </div>
                    {discountType === "customPrice" && (
                      <div className="form-group">
                        <label>Custom Total Price ($):</label>
                        <input
                          type="number"
                          min="0"
                          value={customPrice || ""}
                          onChange={(e) =>
                            setCustomPrice(parseFloat(e.target.value) || 0)
                          }
                        />
                      </div>
                    )} */}
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={child.customPriceEnabled}
                          onChange={(e) =>
                            handleInputChange(
                              childIndex,
                              "customPriceEnabled",
                              e.target.checked
                            )
                          }
                        />
                        Custom Price
                      </label>

                      {child.customPriceEnabled && (
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={child.customPrice ?? ""}
                          onChange={(e) =>
                            handleInputChange(
                              childIndex,
                              "customPrice",
                              e.target.value === ""
                                ? null
                                : parseFloat(e.target.value)
                            )
                          }
                          placeholder="Enter custom price "
                        />
                      )}
                      <div className="form-group" style={{ marginTop: 12 }}>
                        <label>
                          <input
                            type="checkbox"
                            checked={discountType === "noTax"}
                            onChange={(e) =>
                              setDiscountType(e.target.checked ? "noTax" : "")
                            }
                          />
                          Remove Tax
                        </label>
                      </div>
                      <div className="form-group" style={{ marginTop: 6 }}>
                        <label>
                          <input
                            type="checkbox"
                            checked={freeEntry}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setFreeEntry(checked);
                              if (checked) {
                                setChildren((prev) =>
                                  (prev || []).map((c) => ({
                                    ...c,
                                    paymentMethod: "",
                                  }))
                                );
                              }
                            }}
                          />
                          Free Entry
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="add-child-button"
                onClick={handleAddChild}
              >
                + Add Child
              </button>

              <div className="total-amount">
                <h3>Total Amount: ${calculateTotalAmount().toFixed(2)}</h3>
              </div>
              <button type="submit" className="submit-button">
                Add Transaction
              </button>
            </form>
          </div>
        )}
        {activeTab === "dailyTransactions" && (
          <div className="daily-transactions-tab">
            <DailyTransactions
              transactions={transactions}
              onTransactionsPatched={setTransactions}
              onDateChange={loadTransactionsForDate}
            />
          </div>
        )}
        {activeTab === "privateEvents" && (
          <div className="private-events-tab">
            <PrivateEvents
              currentUser={currentUser}
              prefill={privateEventPrefill}
              onPrefillConsumed={() => setPrivateEventPrefill(null)}
              onGenerateInvoice={handleGenerateInvoiceFromEvent}
              onGenerateDepositConfirmation={
                handleGenerateDepositConfirmationFromEvent
              }
            />
          </div>
        )}
        {activeTab === "privateEventNotes" && (
          <div className="private-event-notes-tab">
            <PrivateEventLeads onBookLead={handleBookPrivateEventLead} />
          </div>
        )}
        {isAdmin && activeTab === "monthlyReport" && (
          <div className="monthly-report-tab">
            <MonthlyReport />
          </div>
        )}
        {isAdmin && activeTab === "dailyAnalysis" && (
          <div className="daily-analysis-tab">
            <DailyAnalysis />
          </div>
        )}
        {isAdmin && activeTab === "foodReport" && (
          <div className="food-report-tab">
            <FoodReport />
          </div>
        )}
        {activeTab === "customerInfo" && (
          <div className="customer-info-tab">
            <CustomerInfo />
          </div>
        )}
        {isAdmin && activeTab === "businessTrends" && (
          <div className="business-trends-tab">
            <BusinessTrends />
          </div>
        )}
        {activeTab === "invoiceGenerator" && (
          <div className="invoice-generator-tab">
            <InvoiceGenerator prefillRequest={invoicePrefillRequest} />
          </div>
        )}
        {activeTab === "depositConfirmationGenerator" && (
          <div className="invoice-generator-tab">
            <DepositConfirmationGenerator
              prefillRequest={depositPrefillRequest}
            />
          </div>
        )}
        {activeTab === "tracker" && (
          <div className="tracker-tab">
            <Tracker />
          </div>
        )}
        {isAdmin && activeTab === "holidayInsights" && (
          <div className="holiday-insights-tab">
            <HolidayInsights />
          </div>
        )}
        {isAdmin && activeTab === "expenses" && (
          <div className="expenses-tab">
            {/* <h2>Expenses</h2> */}
            <p> </p>

            {/* Month/Year filters for the ExpensesPanel */}
            <div
              className="filters"
              style={{ display: "flex", gap: 8, marginBottom: 12 }}
            >
              <select
                value={expensesMonth}
                onChange={(e) => setExpensesMonth(Number(e.target.value))}
              >
                {monthNames.map((name, index) => (
                  <option key={index} value={index + 1}>
                    {name}
                  </option>
                ))}
              </select>

              <input
                type="number"
                min="2000"
                max={new Date().getFullYear() + 1}
                value={expensesYear}
                onChange={(e) => setExpensesYear(Number(e.target.value))}
                style={{ width: 100 }}
              />
            </div>

            {/* Your expenses panel for the chosen month/year */}
            <ExpensesPanel month={expensesMonth} year={expensesYear} />
          </div>
        )}
        {isAdmin && activeTab === "users" && (
          <div className="user-tab">
            <Users currentUser={currentUser} />
          </div>
        )}
        {activeTab === "foodOrders" && (
          <div className="food-orders-tab">
            <h2>Food Orders</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Phone Number:</label>
                <input
                  type="tel"
                  value={foodOrderPhone}
                  onChange={(e) => handleFoodOrderPhoneChange(e.target.value)}
                  placeholder="Enter phone number"
                  required
                />
              </div>
              <div className="form-group">
                <label>Customer Name:</label>
                <input type="text" value={foodOrderName} disabled />
              </div>
              <div className="form-group">
                <label>Payment Method:</label>
                <select
                  value={foodOrderPayment}
                  onChange={(e) => setFoodOrderPayment(e.target.value)}
                >
                  <option value="">Select Payment Method</option>
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={foodNoTax}
                  onChange={(e) => setFoodNoTax(e.target.checked)}
                />
                Remove Tax (Food)
              </label>
            </div>

            <div className="food-quick-add">
              <div className="food-quick-add-header">
                <h3>Quick Add</h3>
                <button
                  type="button"
                  className="food-clear-button"
                  onClick={() => setFoodOrders([])}
                  disabled={foodOrders.length === 0}
                >
                  Clear
                </button>
              </div>
              <input
                className="food-quick-search"
                type="text"
                value={foodQuickQuery}
                onChange={(e) => setFoodQuickQuery(e.target.value)}
                placeholder="Search food..."
              />
              <div className="food-quick-grid" role="list">
                {foodOptions
                  .filter((item) => {
                    const q = foodQuickQuery.trim().toLowerCase();
                    if (!q) return true;
                    return String(item.name || "").toLowerCase().includes(q);
                  })
                  .slice(0, 24)
                  .map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="food-quick-btn"
                      onClick={() => handleQuickAddFood(item)}
                      title="Add 1 to order"
                    >
                      <span className="food-quick-name">{item.name}</span>
                      <span className="food-quick-price">
                        ${Number(item.price || 0).toFixed(2)}
                      </span>
                    </button>
                  ))}
              </div>
              {foodOptions.length > 24 && (
                <div className="food-quick-hint">
                  Showing first 24 matches. Use search to find more.
                </div>
              )}
            </div>

            <h3>Food Selection</h3>
            {foodOrders.map((order, orderIndex) => (
              <div key={orderIndex} className="food-item">
                <select
                  value={order.food}
                  onChange={(e) =>
                    handleFoodOrderChange(orderIndex, "food", e.target.value)
                  }
                >
                  <option value="">Select Food</option>
                  {foodOptions.map((food) => (
                    <option key={food.name} value={food.name}>
                      {food.name} (${food.price})
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min="1"
                  value={order.quantity}
                  onChange={(e) =>
                    handleFoodOrderChange(
                      orderIndex,
                      "quantity",
                      parseInt(e.target.value, 10) || 0
                    )
                  }
                  placeholder="Quantity"
                />
                <div className="food-qty-stepper">
                  <button
                    type="button"
                    className="food-qty-btn"
                    onClick={() => handleAdjustFoodQuantity(orderIndex, -1)}
                    aria-label="Decrease quantity"
                  >
                    −
                  </button>
                  <button
                    type="button"
                    className="food-qty-btn"
                    onClick={() => handleAdjustFoodQuantity(orderIndex, 1)}
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
                <div className="food-total">${order.total.toFixed(2)}</div>
                <button
                  type="button"
                  className="remove-food-button"
                  onClick={() => handleRemoveFoodOrder(orderIndex)}
                >
                  ✖
                </button>
              </div>
            ))}
            <button
              type="button"
              className="add-food-button"
              onClick={handleAddFoodOrder}
            >
              + Add Food
            </button>
            <div className="total-amount">
              <h3>Total Amount: ${calculateFoodTotal().toFixed(2)}</h3>
            </div>
            <button type="submit" onClick={handleFoodTransactionSubmit}>
              Submit Food Order
            </button>
          </div>
        )}
        {isAdmin && activeTab === "inventory" && (
          <div className="inventory-tab">
            <h2>Food Inventory</h2>
            <div className="inventory-list">
              <div className="inventory-header" role="row">
                <div className="inventory-col-name">Item</div>
                <div className="inventory-col-current">Current</div>
                <div className="inventory-col-new">New price</div>
                <div className="inventory-col-actions" aria-hidden="true" />
              </div>
              {foodOptions.map((item) => (
                <div className="inventory-item" key={item.id} role="row">
                  <div className="item-name">{item.name}</div>
                  <div className="item-price">
                    ${parseFloat(item.price).toFixed(2)}
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="New Price"
                    onChange={(e) =>
                      handleUpdateFoodPrice(item.id, parseFloat(e.target.value))
                    }
                  />
                  <button
                    type="button"
                    className="delete-btn"
                    onClick={() => handleDeleteFoodItem(item.id)}
                    aria-label={`Delete ${item.name}`}
                  >
                    🗑
                  </button>
                </div>
              ))}
            </div>
            <form
              className="inventory-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleAddFoodItem();
              }}
            >
              <input
                type="text"
                value={newFoodName}
                onChange={(e) => setNewFoodName(e.target.value)}
                placeholder="New Food Name"
                required
              />
              <input
                type="number"
                step="0.01"
                value={newFoodPrice}
                onChange={(e) => setNewFoodPrice(parseFloat(e.target.value))}
                placeholder="Price"
                required
              />
              <button type="submit" className="add-btn">
                + Add Food Item
              </button>
            </form>
          </div>
        )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default HomePage;
